import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { userPool } from '../config/auth';
import { setTokenGetter } from '../services/authInterceptor';
import { authApi } from '../services/api';

type Role = 'owner' | 'instructor' | 'student';

interface AuthUser {
  sub: string;
  email: string;
  name: string;
  isAdmin: boolean;
  // Per-tenant identity, populated after /api/auth/me resolves. Undefined means
  // not yet hydrated; null fields mean the user has no membership row of that
  // type in the current tenant.
  tenantId?: string;
  role?: Role;
  dancerId?: number | null;
  instructorId?: number | null;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  confirmSignup: (email: string, code: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  updateName: (name: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// JWT payloads are base64url-encoded UTF-8. Plain atob() rejects the url-safe
// alphabet and yields Latin-1, which mangles names like "Lucía" into "LucÃa".
function decodeJwtPayload(token: string): any {
  const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function parseIdToken(idToken: string): AuthUser {
  const payload = decodeJwtPayload(idToken);
  // isAdmin is hydrated from the per-tenant role on /api/auth/me; until then,
  // assume non-admin. Cognito groups are no longer consulted.
  return {
    sub: payload.sub,
    email: payload.email || '',
    name: payload.name || '',
    isAdmin: false,
  };
}

// Returns the JWT `exp` claim (epoch seconds), or null if the token can't be
// decoded. Used by the auth interceptor to skip a Cognito SDK roundtrip when
// the cached token is still comfortably in-flight.
function decodeJwtExp(token: string): number | null {
  try {
    const payload = decodeJwtPayload(token);
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function applyTenantIdentity(user: AuthUser, me: Awaited<ReturnType<typeof authApi.me>>): AuthUser {
  return {
    ...user,
    tenantId: me.tenantId,
    role: me.role,
    dancerId: me.dancer?.id ?? null,
    instructorId: me.instructor?.id ?? null,
    isAdmin: me.role === 'owner',
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Fast-path cache: the most recently observed valid ID token. The token
  // getter prefers this when it's not near expiry; otherwise it falls through
  // to `cognitoUser.getSession`, which auto-refreshes via the refresh token.
  const idTokenRef = useRef<string | null>(null);
  // Dedupes parallel refreshes so N in-flight requests don't trigger N
  // refreshSession calls when the cached token has just expired.
  const pendingRefreshRef = useRef<Promise<string | null> | null>(null);
  // Forward declaration: logout needs to be referenced by getValidIdToken
  // (which is registered in an effect that runs before logout is defined
  // via useCallback below). Holding it in a ref decouples the order.
  const logoutRef = useRef<() => void>(() => {});

  const getValidIdToken = useCallback(async (): Promise<string | null> => {
    // Fast path: cached token good for ≥60s.
    const cached = idTokenRef.current;
    if (cached) {
      const exp = decodeJwtExp(cached);
      if (exp !== null && exp - 60 > Math.floor(Date.now() / 1000)) {
        return cached;
      }
    }

    // Slow path: ask Cognito for a fresh session, deduping concurrent callers.
    if (pendingRefreshRef.current) return pendingRefreshRef.current;

    const refresh = new Promise<string | null>((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        idTokenRef.current = null;
        resolve(null);
        return;
      }
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          // Refresh token is dead too — drop the user so the UI reflects it
          // instead of silently degrading API responses to anonymous shape.
          idTokenRef.current = null;
          logoutRef.current();
          resolve(null);
          return;
        }
        const token = session.getIdToken().getJwtToken();
        idTokenRef.current = token;
        resolve(token);
      });
    }).finally(() => {
      pendingRefreshRef.current = null;
    });

    pendingRefreshRef.current = refresh;
    return refresh;
  }, []);

  // Register the token getter for axios interceptor
  useEffect(() => {
    setTokenGetter(getValidIdToken);
  }, [getValidIdToken]);

  // Attempt session restore on mount
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setIsLoading(false);
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        setIsLoading(false);
        return;
      }

      const token = session.getIdToken().getJwtToken();
      idTokenRef.current = token;
      const baseUser = parseIdToken(token);
      setUser(baseUser);

      // Hydrate per-tenant identity from /api/auth/me. If it 403s the user has
      // no Membership in this tenant — surface that by clearing the user.
      authApi.me().then(
        (me) => setUser(applyTenantIdentity(baseUser, me)),
        () => {
          // Fail open on transient errors; the JWT-only user remains.
        },
      ).finally(() => setIsLoading(false));
    });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: async (session) => {
          const token = session.getIdToken().getJwtToken();
          idTokenRef.current = token;
          const baseUser = parseIdToken(token);
          setUser(baseUser);
          try {
            const me = await authApi.me();
            setUser(applyTenantIdentity(baseUser, me));
          } catch {
            // Treat hydration failure as non-fatal; the JWT user is still set.
          }
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }, []);

  const signup = useCallback(async (email: string, password: string, name: string): Promise<void> => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
    ];

    return new Promise((resolve, reject) => {
      userPool.signUp(email, password, attributes, [], (err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  }, []);

  const confirmSignup = useCallback(async (email: string, code: string): Promise<void> => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  }, []);

  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const confirmForgotPassword = useCallback(
    async (email: string, code: string, newPassword: string): Promise<void> => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

      return new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: () => resolve(),
          onFailure: (err) => reject(err),
        });
      });
    },
    []
  );

  const updateName = useCallback(async (name: string): Promise<void> => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) throw new Error('Not authenticated');

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No session'));
          return;
        }

        const attribute = new CognitoUserAttribute({ Name: 'name', Value: name });
        cognitoUser.updateAttributes([attribute], (err) => {
          if (err) {
            reject(err);
            return;
          }

          // Refresh session to get updated token
          cognitoUser.refreshSession(session.getRefreshToken(), (err: Error | null, newSession: CognitoUserSession) => {
            if (err || !newSession) {
              reject(err || new Error('Failed to refresh session'));
              return;
            }
            const token = newSession.getIdToken().getJwtToken();
            idTokenRef.current = token;
            setUser(parseIdToken(token));
            resolve();
          });
        });
      });
    });
  }, []);

  const logout = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    idTokenRef.current = null;
    setUser(null);
  }, []);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        confirmSignup,
        forgotPassword,
        confirmForgotPassword,
        updateName,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
